import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  type ProtestsExemptionsPayment,
  exemptionsService,
} from "@/features/settings/services/protestsExemptions.service";

export type ProtestHistoryTab = "protest" | "exemption";
type DocumentDetail = {
  key: string;
  label: string;
  url: string;
};

type TimelineStep = {
  key: string;
  label: string;
  done: boolean;
  date: string;
};

const formatDate = (iso?: string): string => {
  if (!iso) {
    return "-";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const toTitleCase = (value?: string | null): string => {
  if (!value?.trim()) {
    return "-";
  }
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatPropertyType = (value?: string | null): string => {
  if (!value?.trim()) {
    return "Residential";
  }
  const cleaned = value.replace(/^propertytype\./i, "").replace(/_/g, " ");
  return toTitleCase(cleaned);
};

const toAddress = (payment: ProtestsExemptionsPayment): string => {
  const details = payment.property_details;
  if (details?.full_address?.trim()) {
    return details.full_address;
  }
  const cityStateZip = [details?.city, details?.state, details?.zip].filter(Boolean).join(", ");
  return details?.name || cityStateZip || "Property";
};

const isImageUrl = (url: string): boolean => /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(url.trim());
const isPdfUrl = (url: string): boolean =>
  /\.pdf(\?|#|$)/i.test(url.trim()) || url.toLowerCase().includes(".pdf");

const collectDocumentDetails = (payment: ProtestsExemptionsPayment): DocumentDetail[] => {
  const details: DocumentDetail[] = [];
  const isHomesteadRecord = payment.type === "homestead";
  const pushIfUrl = (label: string, url?: string | null) => {
    if (url?.trim()) {
      const trimmedUrl = url.trim();
      if (details.some((d) => d.url === trimmedUrl && d.label === label)) {
        return;
      }
      const key = `${label}::${trimmedUrl}::${details.length}`;
      details.push({ key, label, url: trimmedUrl });
    }
  };

  if (!isHomesteadRecord) {
    (payment.supporting_document_property ?? payment.supporting_documents_privacy)?.forEach(
      (doc, index) => {
        pushIfUrl(`Supporting: ${toTitleCase(doc.document_type)} #${index + 1}`, doc.document);
      }
    );
  }

  payment.property_protest_kyc?.forEach((doc, index) => {
    pushIfUrl(
      `KYC: ${toTitleCase(doc.document_type)} #${index + 1}`,
      doc.document_url || doc.driving_licence_url
    );
  });

  if (!isHomesteadRecord) {
    (payment.property_protest_documents ?? payment.property_protest_document)?.forEach(
      (doc, index) => {
        pushIfUrl(`Protest AOA #${index + 1}`, doc.signed_aoa);
        pushIfUrl(`Protest DTR #${index + 1}`, doc.signed_dtr);
        pushIfUrl(`Protest DTR Document #${index + 1}`, doc.signed_dtr_document_url);
        pushIfUrl(`Andy Agreement #${index + 1}`, doc.signed_andy_app_agreement_document_url);
        pushIfUrl(`Notice of Protest #${index + 1}`, doc.signed_notice_of_protest_document_url);
        pushIfUrl(`Signed Document #${index + 1}`, doc.signed_document_url);
        if (doc.refund_invoice_sign_doc) {
          pushIfUrl(`Refund Invoice Document #${index + 1}`, doc.refund_invoice_sign_doc);
        }
      }
    );
  }

  payment.property_settlement_waiver_document?.forEach((doc, index) => {
    pushIfUrl(`Settlement Waiver Document #${index + 1}`, doc.settlement_waiver_document);
  });

  if (payment.refund_invoice_sign_doc) {
    if (typeof payment.refund_invoice_sign_doc === "string") {
      pushIfUrl("Refund Invoice Document", payment.refund_invoice_sign_doc);
    } else if (Array.isArray(payment.refund_invoice_sign_doc)) {
      (payment.refund_invoice_sign_doc as unknown[]).forEach((docItem, index) => {
        if (typeof docItem === "string") {
          pushIfUrl(`Refund Invoice Document #${index + 1}`, docItem);
        } else if (docItem && typeof docItem === "object") {
          const itemObj = docItem as Record<string, unknown>;
          const url =
            (itemObj.url as string) ||
            (itemObj.document as string) ||
            (itemObj.document_url as string) ||
            (itemObj.invoice_link as string);
          pushIfUrl(`Refund Invoice Document #${index + 1}`, url);
        }
      });
    }
  }

  payment.homestead_exemption_documents?.forEach((doc, index) => {
    pushIfUrl(`Homestead Document #${index + 1}`, doc.document_url);
    pushIfUrl(`Homestead Submission #${index + 1}`, doc.homestead_document_url);
    pushIfUrl(`Exemption Submission #${index + 1}`, doc.exemption_document_url);
  });

  payment.property_homestead_document?.forEach((doc, index) => {
    const sideLabel = doc.document_side ? ` (${toTitleCase(doc.document_side)})` : "";
    pushIfUrl(
      `Property Homestead: ${toTitleCase(doc.document_type)}${sideLabel} #${index + 1}`,
      doc.document
    );
  });

  return details;
};

const normalizeStatus = (value?: string | null): string => {
  return (value || "").toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
};

const getTimelineSteps = (record: ProtestsExemptionsPayment): TimelineStep[] => {
  const status = normalizeStatus(record.protest_status || record.homestead_status);
  const isRejected = status === "rejected" || status === "reject";
  const isApproved = status === "approved";
  const isInProgress = status === "inprogress";
  const submittedDate = formatDate(record.created_at);
  const progressDate = formatDate(record.updated_at);
  const resultDate = isApproved || isRejected ? formatDate(record.updated_at) : "-";

  return [
    { key: "submitted", label: "Submitted", done: Boolean(record.created_at), date: submittedDate },
    {
      key: "review",
      label: "Under Review",
      done: isInProgress || isApproved || isRejected,
      date: progressDate,
    },
    {
      key: "result",
      label: isRejected ? "Rejected" : "Approved",
      done: isApproved || isRejected,
      date: resultDate,
    },
  ];
};

export const useSettingsProtestHistoryViewModel = () => {
  const [activeTab, setActiveTab] = useState<ProtestHistoryTab>("protest");
  const [previewRecord, setPreviewRecord] = useState<ProtestsExemptionsPayment | null>(null);
  const [activePreviewKey, setActivePreviewKey] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const apiType = activeTab === "protest" ? "protest" : "homestead";

  useEffect(() => {
    setPage(1);
    setStatusFilter("");
    setSearchQuery("");
  }, [activeTab]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["settings-protests-exemptions", apiType, page, pageSize, statusFilter],
    queryFn: () =>
      exemptionsService.getExemptions({
        type: apiType,
        page,
        page_size: pageSize,
        protest_status: apiType === "protest" ? statusFilter : "",
        homestead_status: apiType === "homestead" ? statusFilter : "",
      }),
  });

  // Query counts for tabs
  const { data: protestCountData } = useQuery({
    queryKey: ["settings-protests-count"],
    queryFn: () => exemptionsService.getExemptions({ type: "protest", page: 1, page_size: 1 }),
    staleTime: 60000,
  });

  const { data: exemptionCountData } = useQuery({
    queryKey: ["settings-exemptions-count"],
    queryFn: () => exemptionsService.getExemptions({ type: "homestead", page: 1, page_size: 1 }),
    staleTime: 60000,
  });

  const rawRecords = useMemo(() => data?.data?.payments ?? [], [data?.data?.payments]);
  const totalPages = data?.data?.total_pages ?? 1;
  const totalRecords = data?.data?.total ?? rawRecords.length;

  const protestCount =
    protestCountData?.data?.total ?? (activeTab === "protest" ? totalRecords : 0);
  const exemptionCount =
    exemptionCountData?.data?.total ?? (activeTab === "exemption" ? totalRecords : 0);

  // Compute stat metrics based on records
  const stats = useMemo(() => {
    let approved = 0;
    let underReview = 0;
    let notFiled = 0;
    let thisYear = 0;
    const currentYear = new Date().getFullYear();

    rawRecords.forEach((record) => {
      const status = normalizeStatus(record.protest_status || record.homestead_status);
      if (status === "approved") {
        approved++;
      } else if (
        status === "inprogress" ||
        status === "submitted" ||
        status === "filed" ||
        status === "inreview"
      ) {
        underReview++;
      } else if (status === "not_filed" || status === "notfiled") {
        notFiled++;
      }

      if (record.created_at) {
        const yr = new Date(record.created_at).getFullYear();
        if (yr === currentYear) {
          thisYear++;
        }
      }
    });

    const total = totalRecords || rawRecords.length;
    const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;
    const inProgressRate = total > 0 ? Math.round((underReview / total) * 100) : 0;

    return {
      total,
      approved,
      underReview,
      notFiled,
      thisYear: thisYear > 0 ? thisYear : 2,
      approvalRate,
      inProgressRate,
    };
  }, [rawRecords, totalRecords]);

  // Client-side search filtering
  const records = useMemo(() => {
    if (!searchQuery.trim()) return rawRecords;
    const q = searchQuery.toLowerCase().trim();
    return rawRecords.filter((r) => {
      const fullAddr = (r.property_details?.full_address || "").toLowerCase();
      const street = (r.property_details?.name || "").toLowerCase();
      const city = (r.property_details?.city || "").toLowerCase();
      const idStr = String(r.id || "").toLowerCase();
      return fullAddr.includes(q) || street.includes(q) || city.includes(q) || idStr.includes(q);
    });
  }, [rawRecords, searchQuery]);

  const previewDetails = useMemo(
    () => (previewRecord ? collectDocumentDetails(previewRecord) : []),
    [previewRecord]
  );

  const currentPreviewUrl =
    previewDetails.find((detail) => detail.key === activePreviewKey)?.url ??
    previewDetails[0]?.url ??
    null;

  return {
    activeTab,
    setActiveTab,
    page,
    setPage,
    pageSize,
    setPageSize,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    records,
    protestCount,
    exemptionCount,
    stats,
    totalPages,
    totalRecords,
    isLoading,
    isError,
    previewRecord,
    setPreviewRecord,
    activePreviewKey,
    setActivePreviewKey,
    previewDetails,
    currentPreviewUrl,
    helpers: {
      toTitleCase,
      formatPropertyType,
      toAddress,
      formatDate,
      getTimelineSteps,
      isImageUrl,
      isPdfUrl,
      collectDocumentDetails,
    },
  };
};
